import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { delay, escapeHtml } from '../../modules/utils'
import { EventSeriesAutocomplete } from '../../modules/event-series-search'
import { is } from 'date-fns/locale'

Locale.getInstance().registerKeySet('fr', 'lvc-eventSerie-search-field', {
  title: "Nom de l'événement",
  placeholder: 'Rechercher un événement',
  choose: 'Choisir',
  createEvent: 'Créer un événement',
  tooltip: 'Vous pouvez rechercher un événement en français ou en anglais.',
})

Locale.getInstance().registerKeySet('en', 'lvc-eventSerie-search-field', {
  title: 'Event name',
  placeholder: 'Search for a eventSerie',
  choose: 'Choose',
  createEvent: 'Create an eventSerie',
  tooltip: 'You can search for an eventSerie in French or English.',
})

export interface LvcEventSeriesSearchField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcEventSeriesSearchField extends MinzeElement {
  private eventSeries: any[] = []
  private selectedId: string = ''
  private filter: string = ''
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', '']]

  reactive: Reactive = [
    ['value', ''],
    ['tooltipOpened', false],
  ]

  tooltipElement: HTMLElement | null = null

  static observedAttributes = ['title', 'on-change']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    this.eventSeries = await AdditionalTypes.getInstance().getAll()
    this.tooltipElement = this.select('#eventSeries-tooltip')

    const list = (await EventSeriesAutocomplete.instance.getSuggestions(this.filter)) as any[]
    this.eventSeries = list || []

    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedId = JSON.parse(decodeURIComponent(this.defaultValue)).map((k: any) => k.id)
    }

    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    const filterElement = this.select('input[name="filter"]') as HTMLInputElement
    this.rerender()
    // Need to wait for the rerender to complete before focusing the input otherwise it won't work
    await delay(0)
    filterElement.focus()
  }

  getSelectedValuesLabels() {
    const selectedEvent = this.eventSeries.find(
      eventSerie => `${eventSerie.id}` === this.selectedId
    )
    return selectedEvent
      ? Locale.language === 'en'
        ? selectedEvent.nameEn
        : selectedEvent.nameFr
      : ''
  }

  onEventClick(e: Event): void {
    e.preventDefault()
    const clickedId = (e.target as HTMLElement).getAttribute('data-id')

    if (!clickedId) {
      return
    }

    if (this.selectedId === clickedId) {
      this.selectedId = ''
    } else {
      this.selectedId = clickedId
    }
    this.onToggle()
    this.serializeValue()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    const list = (await EventSeriesAutocomplete.instance.getSuggestions(this.filter)) as any[]
    this.eventSeries = list || []
    this.rerender()
  }

  serializeValue() {
    let objects = this.eventSeries.filter(k => this.selectedId.indexOf(k.id) > -1)
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }
  getEventsLabel() {
    if (this.selectedId === '') {
      return `
        <span>
          ${
            this.defaultValue && this.defaultValue !== true
              ? this.defaultValue
              : Locale.getKey('lvc-eventSerie-search-field.placeholder')
          }
        </span>
      `
    }

    return this.getSelectedValuesLabels()
  }

  renderEvent = (eventSerie: any) => {
    let eventIdString = `${eventSerie.id}`
    let isNotSelected = this.selectedId !== eventIdString

    return `
      <button
        name="eventSerie"
        class="${isNotSelected ? 'not-selected' : 'selected'}"
        data-id="${eventSerie.id}">
        ${Locale.language === 'en' ? eventSerie.nameEn : eventSerie.nameFr}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderEvents = () => {
    let html = ''

    for (let i = 0; i < this.eventSeries.length; i++) {
      html += this.renderEvent(this.eventSeries[i])
    }

    return html
  }

  openTooltip = () => {
    this.tooltipOpened = true
  }

  closeTooltip = () => {
    this.tooltipOpened = false
  }

  html = () => {
    return `
      <div class="title">
        ${this.title || Locale.getKey('lvc-eventSerie-search-field.title')}
        <lvc-help-icon name="helpIcon"></lvc-help-icon>
      </div>
      ${this.tooltipOpened ? `<div class="close-overlay"></div>` : ''}
      <dialog id="eventSeries-tooltip" class="tooltip ${this.tooltipOpened ? 'open' : ''}">
        <div class="eventSeries-tooltip-content" slot="tooltip-content">
          <lvc-text font-preset="small">${Locale.getKey('lvc-eventSerie-search-field.tooltip')}</lvc-text>
        </div>
      </dialog>
      <div class="input ${this.selectedId !== '' ? 'has-value' : ''}">
        <button class="dropbtn" name="toggle">
          <lvc-search-icon></lvc-search-icon>
          ${this.getEventsLabel()}
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
          <lvc-search-icon></lvc-search-icon>
            <input name="filter" type="text" value="${escapeHtml(this.filter)}" placeholder="${this.placeholder || Locale.getKey('lvc-eventSerie-search-field.placeholder')}">
          </div>
          <div class="eventSeries" data-selected="${this.selectedId}">
            ${this.renderEvents()}
          </div>
          <div class="footer">
            <button class="createEvent">
              <lvc-add-icon></lvc-add-icon>
              ${Locale.getKey('lvc-eventSerie-search-field.createEvent')}
            </button>
          </div>
        </div>
      </div>
    `
  }

  css = () => {
    return `
      :host {
      }

      lvc-circle-check-select-icon {
        display: flex;
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }

      .overlay.show {
        display: block;
      }

      .footer {
        display: flex;
        height: 60px;
        padding: 0 10px;
      }

      .tooltip-dialog {
        display: none;
        position: absolute;
        background-color: #fff;
        border: 1px solid #ccc;
        padding: 10px;
        z-index: 3; /* Ensure the dialog appears above other elements */
        /* Add any other styles for the dialog */
      }

      dialog.open {
        margin-top: 5px;
        display: block;
        z-index: 1;
        border: 1px solid rgba(30, 0, 125, 0.3);
        box-shadow: 0px 4px 16px rgba(15, 0, 62, 0.15);
        border-radius: 8px;
        padding: 16px;
        font-weight: ${Font.weight.medium};
        font-size: 14px;
        line-height: 150%;
        color: #1E007D;
        max-width: 90vw;
        width: 340px;
        margin-left: auto;
      }

      lvc-add-icon {
        display: flex;
        align-items: center;
      }

      .createEvent {
        border-radius: 999px;
        color: #ffff;
        background: var(--Minuit, #1E007D);
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        display: flex;
        padding: 10px 16px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex: 1 1 0;
        border: none;
        margin: 10px;
        cursor: pointer;
      }

      .header {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: #ffff;
      }

      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
        display: flex;


        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 4px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
        cursor: text;
      }

      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .eventSeries {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .eventSeries button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: fit-content;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
        align-items: center;
      }

      .eventSeries button:last-child {
        border-bottom: none;
      }

      .eventSeries button.selected {
      }

      .eventSeries-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
        list-style: none; /* Remove default list style */
      }

      .eventSeries-tooltip-content li {
        font-size: 14px;
        font-weight: 500;
        line-height: 21px;
        letter-spacing: 0px;
        text-align: left;
      }

      .eventSeries-tooltip-content li::before {
        content: ''; /* Empty content to remove default bullet point */
        display: inline-block;
        width: 9px;
        background-color: red;
        height: 10px;
        margin-right: 8px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none" stroke="%231E007D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9C6.72234 7.77224 5.49206 6.47963 4.31317 5.1268M4.31317 5.1268C3.16768 3.81229 2.04474 2.42401 1 1M4.31317 5.1268C5.39654 3.6513 6.68118 2.41964 8 1M4.31317 5.1268C3.06821 6.63983 2.15598 7.52691 1 9" /></svg>') no-repeat center center;
      }

      .eventSeries-tooltip-content h3 {
        margin: 0 0 8px;
      }

      .eventSeries-tooltip-content ul {
        margin: 0;
        padding-left: 15px;
      }

      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 14px;
        font-weight: 500;
        border: none;
        cursor: text;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: flex-start;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      lvc-search-icon {
        margin-right: 12px;
        display:flex;
        align-items: center;
      }

      lvc-help-icon {
        display:flex;
        align-items: center;
        margin-left: 4px;
        cursor: pointer;
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #ffff;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }

      .close-overlay {
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 1;
      }
    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="eventSerie"]', 'click', this.onEventClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
    ['[name="helpIcon"]', 'click', this.openTooltip],
    ['.close-overlay', 'click', this.closeTooltip],
  ]
}
